import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../utils/api';
import { STATUS } from '../../../utils/status';
import { ConfigPayload, ConfigState, GetPayload, getQuestionAnalysisPayload, UploadPayload } from '../../interfaces/interface';
import extraReducers from '../../extraReducers/configExtraReducer';

const initialState: ConfigState = {
  config: {
    count: undefined,
    results: [],
    data: [],
    filter: undefined,
  },
  configData: {
    message: '',
    data: null,
    error: null
  },
  configCategories: [],
  currentConfig: [],
  error: null,
  createStatus: STATUS.NOT_STARTED,
  deleteStatus: STATUS.NOT_STARTED,
  updateStatus: STATUS.NOT_STARTED,
  getStatus: STATUS.NOT_STARTED,
  getConfigStatus: STATUS.NOT_STARTED,
  getConfigCategoryListStatus: STATUS.NOT_STARTED,
  getConfigDataStatus: STATUS.NOT_STARTED,
  createConfigMessage: "",
  updateConfigMessage: "",
  getConfigMessage: "",
  getConfigForTableMessage: "",
  getConfigCategoryListMessage: "",
  getConfigsDataMessage: "",
  getQuestionAnalysisStatus: STATUS.NOT_STARTED,
  QuestionAnalysisData: {
    data: []
  },
  getStageWiseConfigStatus: STATUS.NOT_STARTED,
  getStageWiseConfigData: {
    count: 0,
    data: {results: []}
  },
  getStageWiseConfigMessage: ""

};

export const createConfig = createAsyncThunk(
  'project-configs/POST',
  async (payload: any, thunkAPI) => {
    try {
      const response = await api.post('project/company/config/', payload);
      const { status, data } = response;

      if (status === 201) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      console.log(error)
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message });
    }
  }
)

export const updateConfig = createAsyncThunk(
  'project-configs/patch',
  async (payload: any, thunkAPI) => {
    try {
      const response = await api.patch('project/company/config/', payload);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        return thunkAPI.rejectWithValue({ message: error?.response?.data?.message });
      }
    }
  }
)

export const getConfig = createAsyncThunk(
  'project-configs/get',
  async (payload: ConfigPayload = {}, thunkAPI) => {
    try {
      const { task_category, category } = payload;
      const queryString = new URLSearchParams({
        ...(task_category && { task_category }),
        ...(category && { category }),
      }).toString();
      const response = await api.get(`project-configs/collection?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage });
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getConfigForTable = createAsyncThunk(
  'project/company/configs/get',
  async (payload: UploadPayload = { project_id: '' }, thunkAPI) => {
    try {
      const {
        search,
        page,
        pageSize,
        sort,
        order,
        ordering,
        project_id,
        id,
        type,
        stage_id,
        collection_ids,
        paginate,
      } = payload;
      const queryString = new URLSearchParams({
        ...(search && { search }),
        ...(page && { page: page.toString() }),
        ...(pageSize && { page_size: pageSize.toString() }),
        ...(sort && { sort }),
        ...(order && { order }),
        ...(ordering && { ordering }),
        ...(id && { id }),
        ...(project_id && { project_id }),
        ...(type && { type }),
        ...(stage_id && { stage_id }),
        ...(collection_ids && { collection_ids }),
        ...(paginate !== undefined && { paginate: String(paginate) }),
      }).toString();
      const response = await api.get(`project/company/config/?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getConfigCategoryList = createAsyncThunk(
  'project/company/get_task_columns/get',
  async (payload: UploadPayload = { project_id: '' }, thunkAPI) => {
    try {
      const { project_id } = payload;
      const queryString = new URLSearchParams({
        ...(project_id && { project_id }),
      }).toString();
      const response = await api.get(`/project/company/get_task_columns?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage });
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);


export const getConfigsData = createAsyncThunk(
  'task/company/export/get',
  async (payload: UploadPayload = { project_id: '' }, thunkAPI) => {
    try {
      const { page, pageSize, project_id, type } = payload;

      const queryString = new URLSearchParams({
        ...(project_id && { project_id: String(project_id) }),
      }).toString();

      const response = await api.get(`task/company/data-dump/?${queryString}`);

      const { status, data } = response;

      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }

    } catch (error: any) {
      const truncatedMessage = `${error.response.data.message.slice(0, 100)}...`;
      return thunkAPI.rejectWithValue({ message: truncatedMessage })
    }
  }
);


export const getStageWiseConfigs = createAsyncThunk(
  'project/company/stage/previous/get',
  async (payload: UploadPayload = { id: '' }, thunkAPI) => {
    try {
      const { id } = payload;

      const queryString = new URLSearchParams({
        ...(id && { id: id }),
      }).toString();

      const response = await api.get(`project/company/stage/previous?${queryString}`);
      const { status, data } = response;

      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }

    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error.message })
    }
  }
);

export const getQuestionAnalysis = createAsyncThunk(
  'project/question-wise-analysis/get',
  async (payload: getQuestionAnalysisPayload = { project_id: '', difficulty_level: '', question_id: '' }, thunkAPI) => {
    try {
      const { project_id, difficulty_level, question_id } = payload;
      const queryString = new URLSearchParams({
        ...(project_id && { project_id }),
        ...(difficulty_level && { difficulty_level }),
        ...(question_id && { question_id }),
      }).toString();
      const response = await api.get(`project/question-wise-analysis/?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    updateConfigData(state, action: PayloadAction<any>) {
      state.config = { ...state.config, ...action.payload };
    },
    getConfigData(state, action) {
      state.currentConfig = action.payload;
    },
    setCreateConfigStatus(state, action) {
      state.createStatus = action.payload
    },
    setUpdateConfigStatus(state, action) {
      state.updateStatus = action.payload
    },
    setGetConfigDataStatus(state, action) {
      state.getConfigDataStatus = action.payload
    },
    resetCurrentConfig(state) {
      state.currentConfig = [];
    },


  },
  extraReducers
});

export const { updateConfigData, setCreateConfigStatus, getConfigData, setUpdateConfigStatus, setGetConfigDataStatus, resetCurrentConfig } = configSlice.actions;
export default configSlice.reducer;
